import { useEffect, useRef } from 'react';

/**
 * Hook to warn users when they try to leave the page with unsaved changes.
 * Covers all navigation types:
 * - Tab close / refresh → beforeunload
 * - Browser back/forward → popstate with pushState trick
 * - In-app navigation (menu clicks, links) → window.__unsavedChanges flag
 *   checked by Layout.handleNavigation via confirmNavigation()
 *
 * @param {boolean} isDirty - Whether there are unsaved changes
 */
export const useUnsavedChangesWarning = (isDirty) => {
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // Expose dirty state globally so Layout can check before navigating
  useEffect(() => {
    window.__unsavedChanges = isDirty;
    return () => { window.__unsavedChanges = false; };
  }, [isDirty]);

  // Browser-level: tab close, refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Browser back/forward button
  useEffect(() => {
    if (!isDirty) return;

    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (isDirtyRef.current) {
        const leave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
        if (!leave) {
          window.history.pushState(null, '', window.location.href);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty]);
};

/**
 * Check if there are unsaved changes and prompt the user.
 * Call this before any programmatic navigation (navigate(), link clicks).
 * @returns {boolean} true if safe to navigate, false if user cancelled
 */
export const confirmNavigation = () => {
  if (window.__unsavedChanges) {
    return window.confirm('You have unsaved changes. Are you sure you want to leave?');
  }
  return true;
};
